<script lang="ts">
  import { page } from '$app/stores'
  import type { Faqs, Sections } from '@bruce/api/common/faq'
  import {
    Button,
    ExpansionPanel,
    Input,
    List,
    ListGroup,
    Markdown,
    type Language,
  } from '@bruce/components'
  import { STRINGS } from '$lib/constants/strings'
  import { utils } from '$lib/utils'
  import { browser } from '$app/environment'

  export let faqs: Faqs
  export let sections: Sections | undefined = undefined
  export let search = false
  export let contact = false

  let searchValue = $page.url.searchParams.get('search') ?? ''
  let searchResult: Faqs | undefined = undefined
  let timeout: ReturnType<typeof setTimeout>

  $: filteredFaqs = searchResult ?? faqs
  $: filteredSections = sections?.filter(
    (section) =>
      filteredFaqs.filter((faq) => faq.section_id === section.id).length > 0,
  )
  $: lang = $page.params.lang as Language

  $: searchValue === ''
    ? resetSearch()
    : (timeout = utils.debounce(searchFaq, 500, timeout))

  async function searchFaq() {
    await utils.setSearchQueryParam(
      browser,
      $page.url.searchParams,
      searchValue,
    )

    searchResult = faqs.filter(
      (faq) =>
        faq?.question?.toLowerCase().includes(searchValue.toLowerCase()) ||
        faq?.answer?.toLowerCase().includes(searchValue.toLowerCase()),
    )
  }

  async function resetSearch() {
    await utils.setSearchQueryParam(
      browser,
      $page.url.searchParams,
      searchValue,
    )

    searchResult = undefined
  }
</script>

<section>
  {#if search}
    <Input
      type="search"
      placeholder={STRINGS[lang].FAQ_SEARCH}
      rounded
      variant="outlined"
      bind:value={searchValue}
    />
  {/if}
  {#if searchValue && searchResult && (searchResult.length === 0 || filteredSections?.length === 0)}
    <p class="no-match">{STRINGS[lang].FAQ_NO_RESULTS}</p>
  {:else if filteredSections && filteredSections.length > 0}
    <ul>
      {#each filteredSections as section (section.id)}
        <li>
          <ExpansionPanel title={section.title} open={Boolean(searchResult)}>
            <List>
              {#each filteredFaqs.filter((faq) => faq.section_id === section.id) as faq (faq.id)}
                {#if faq.question && faq.answer}
                  <ListGroup value={faq.question} open={Boolean(searchResult)}>
                    <li class="answer">
                      <Markdown source={faq.answer} isInline matchApp />
                    </li>
                  </ListGroup>
                {/if}
              {/each}
            </List>
          </ExpansionPanel>
        </li>
      {/each}
    </ul>
  {:else}
    <ul>
      {#each filteredFaqs as faq (faq.id)}
        {#if faq.question && faq.answer}
          <li>
            <ExpansionPanel title={faq.question} open={Boolean(searchResult)}>
              <li class="answer">
                <Markdown source={faq.answer} matchApp />
              </li>
            </ExpansionPanel>
          </li>
        {/if}
      {/each}
    </ul>
  {/if}

  {#if contact}
    <div class="contact-us">
      <p>{STRINGS[lang].FAQ_CONTACT_DESCRIPTION}</p>
      <Button
        type="button"
        rounded
        prependIcon="ph-chats-circle"
        variant="flat"
        on:click={() => window.Intercom('show')}
      >
        {STRINGS[lang].FAQ_CONTACT_BUTTON}
      </Button>
    </div>
  {/if}
</section>

<style lang="scss">
  section {
    display: flex;
    flex-direction: column;
    gap: 2em;
    margin-top: 2em;
    max-width: 800px;
    width: 100%;

    ul {
      display: flex;
      flex-direction: column;
      gap: 1em;
      margin: 0;
      padding: 0;
      list-style-type: none;

      :global(summary) {
        border-radius: 1em;
      }

      .answer {
        margin: 1em 0 1em 0.75em;

        :global(p) {
          font-size: 16px;
        }
      }
    }

    .no-match {
      text-align: center;
    }

    .contact-us {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
</style>
